import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Sparkles, Edit, X, Save, Library } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import EmailTemplateGallery from "./EmailTemplateGallery";
import { EmailTemplate } from "@/data/emailTemplates";

interface AICampaignGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCampaignCreated?: () => void;
}

interface GeneratedStep {
  step_number: number;
  subject_line: string;
  email_body: string;
  delay_days: number;
  personalization_tokens?: string[];
}

const CAMPAIGN_TEMPLATES = [
  { value: "post_storm", label: "Post-Storm Follow-up", prompt: "Create a campaign to reach out to homeowners after a recent storm event, offering free roof inspections and insurance claim assistance" },
  { value: "cold_outreach", label: "Cold Outreach", prompt: "Create a campaign for cold outreach to homeowners in areas with aging roofs, highlighting quality materials and warranties" },
  { value: "reengagement", label: "Re-engagement", prompt: "Create a campaign to re-engage old leads who showed interest but never converted, offering special seasonal promotions" },
  { value: "referral", label: "Referral Request", prompt: "Create a campaign to request referrals from satisfied customers, incentivizing them with discounts on future services" },
  { value: "custom", label: "Custom Campaign", prompt: "" }
];

export default function AICampaignGenerator({ open, onOpenChange, onCampaignCreated }: AICampaignGeneratorProps) {
  const [template, setTemplate] = useState("post_storm");
  const [prompt, setPrompt] = useState(CAMPAIGN_TEMPLATES[0].prompt);
  const [loading, setLoading] = useState(false);
  const [generatedCampaign, setGeneratedCampaign] = useState<any>(null);
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editedSteps, setEditedSteps] = useState<GeneratedStep[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);

  const handleTemplateChange = (value: string) => {
    setTemplate(value);
    const selectedTemplate = CAMPAIGN_TEMPLATES.find(t => t.value === value);
    if (selectedTemplate) {
      setPrompt(selectedTemplate.prompt);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a campaign description");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-sales-campaign", {
        body: { 
          prompt, 
          campaignType: "email_sequence",
          images: uploadedImages 
        }
      });

      if (error) throw error;

      setGeneratedCampaign(data);
      setEditedSteps(data.steps);
      toast.success("Campaign generated successfully!");
    } catch (error: any) {
      console.error("Error generating campaign:", error);
      toast.error(error.message || "Failed to generate campaign");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!generatedCampaign) return;

    setLoading(true);
    try {
      // Update campaign status to approved
      const { error } = await supabase
        .from("ai_campaigns")
        .update({ 
          status: "approved",
          approved_at: new Date().toISOString()
        })
        .eq("id", generatedCampaign.campaign.id);

      if (error) throw error;

      // Update sequence status to active
      await supabase
        .from("email_sequences")
        .update({ status: "active" })
        .eq("id", generatedCampaign.sequence.id);

      toast.success("Campaign approved and activated!");
      onOpenChange(false);
      onCampaignCreated?.();
      setGeneratedCampaign(null);
      setPrompt(CAMPAIGN_TEMPLATES[0].prompt);
    } catch (error: any) {
      console.error("Error approving campaign:", error);
      toast.error(error.message || "Failed to approve campaign");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    setGeneratedCampaign(null);
    setEditedSteps([]);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) throw new Error("No organization found");

      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${profile.org_id}/${Date.now()}-${Math.random()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('campaign-images')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('campaign-images')
          .getPublicUrl(fileName);

        return publicUrl;
      });

      const urls = await Promise.all(uploadPromises);
      setUploadedImages(prev => [...prev, ...urls]);
      toast.success(`${urls.length} image(s) uploaded successfully`);
    } catch (error: any) {
      console.error("Error uploading images:", error);
      toast.error(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (url: string) => {
    try {
      // Extract file path from public URL
      const path = url.split('/campaign-images/')[1];
      if (path) {
        const { error } = await supabase.storage
          .from('campaign-images')
          .remove([path]);
        
        if (error) throw error;
      }
      
      setUploadedImages(prev => prev.filter(img => img !== url));
      toast.success("Image removed");
    } catch (error: any) {
      console.error("Error removing image:", error);
      toast.error("Failed to remove image");
    }
  };

  const handleEditStep = (idx: number, field: keyof GeneratedStep, value: any) => {
    setEditedSteps(prev => prev.map((step, i) => 
      i === idx ? { ...step, [field]: value } : step
    ));
  };

  const handleGenerateAIImages = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a campaign description first");
      return;
    }

    setGeneratingImages(true);
    try {
      // Generate 3 campaign images using AI
      const imagePrompts = [
        `Professional roofing company team photo, high quality, modern`,
        `Beautiful residential roof installation, before and after comparison, professional photography`,
        `Storm damage on residential roof, detailed close-up, professional documentation`
      ];

      const { data, error } = await supabase.functions.invoke("generate-campaign-images", {
        body: { 
          prompts: imagePrompts,
          campaignDescription: prompt 
        }
      });

      if (error) throw error;

      setUploadedImages(prev => [...prev, ...data.images]);
      toast.success(`${data.images.length} AI images generated!`);
    } catch (error: any) {
      console.error("Error generating images:", error);
      toast.error(error.message || "Failed to generate images");
    } finally {
      setGeneratingImages(false);
    }
  };

  const handleTemplateSelect = async (template: EmailTemplate) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) throw new Error("No organization found");

      // Create campaign from template
      const { data: campaign, error: campaignError } = await supabase
        .from("ai_campaigns")
        .insert({
          org_id: profile.org_id,
          created_by: user.id,
          name: template.name,
          description: template.description,
          campaign_type: "email_sequence",
          prompt_used: `Template: ${template.name}`,
          status: "draft",
          ai_model_version: "template"
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      // Create sequence
      const { data: sequence, error: sequenceError } = await supabase
        .from("email_sequences")
        .insert({
          org_id: profile.org_id,
          created_by: user.id,
          name: template.name,
          description: template.description,
          status: "draft",
          total_steps: template.steps.length
        })
        .select()
        .single();

      if (sequenceError) throw sequenceError;

      // Create steps
      const stepsToInsert = template.steps.map(step => ({
        sequence_id: sequence.id,
        step_number: step.step,
        subject: step.subject,
        body: step.body,
        delay_days: step.delayDays,
        delay_hours: step.delayHours || 0,
        step_type: "email"
      }));

      const { error: stepsError } = await supabase
        .from("sequence_steps")
        .insert(stepsToInsert);

      if (stepsError) throw stepsError;

      // Create variant
      await supabase
        .from("campaign_variants")
        .insert({
          campaign_id: campaign.id,
          sequence_id: sequence.id,
          variant_name: "A",
          is_control: true,
          traffic_percentage: 100
        });

      // Format for display
      setGeneratedCampaign({
        campaign,
        sequence,
        steps: template.steps.map(s => ({
          step_number: s.step,
          subject_line: s.subject,
          email_body: s.body,
          delay_days: s.delayDays
        }))
      });
      setEditedSteps(template.steps.map(s => ({
        step_number: s.step,
        subject_line: s.subject,
        email_body: s.body,
        delay_days: s.delayDays
      })));

      toast.success("Template loaded successfully!");
    } catch (error: any) {
      console.error("Error loading template:", error);
      toast.error(error.message || "Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStep = async (idx: number) => {
    if (!generatedCampaign) return;

    setLoading(true);
    try {
      const step = editedSteps[idx];
      
      // Update the sequence step in database
      const { error } = await supabase
        .from("sequence_steps")
        .update({
          subject: step.subject_line,
          body: step.email_body,
          delay_days: step.delay_days
        })
        .eq("sequence_id", generatedCampaign.sequence.id)
        .eq("step_number", step.step_number);

      if (error) throw error;

      toast.success("Step updated successfully");
      setEditingStep(null);
    } catch (error: any) {
      console.error("Error saving step:", error);
      toast.error(error.message || "Failed to save changes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EmailTemplateGallery
        open={showTemplateGallery}
        onOpenChange={setShowTemplateGallery}
        onSelectTemplate={handleTemplateSelect}
      />
      
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Campaign Generator
            </DialogTitle>
            <DialogDescription>
              Choose a professional template or let AI generate a custom email sequence
            </DialogDescription>
          </DialogHeader>

          {!generatedCampaign ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTemplateGallery(true)}
                  className="gap-2 flex-1"
                >
                  <Library className="w-4 h-4" />
                  Browse Professional Templates
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or generate with AI</span>
                </div>
              </div>
            <div className="space-y-2">
              <Label>Campaign Template</Label>
              <Select value={template} onValueChange={handleTemplateChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_TEMPLATES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Campaign Description</Label>
              <Textarea
                placeholder="Describe your campaign goals, target audience, and key messages..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
                className="resize-none"
              />
              <p className="text-sm text-muted-foreground">
                Example: "Create a 5-email sequence targeting homeowners affected by recent hail storms, emphasizing our insurance claim expertise and free inspections"
              </p>
            </div>

            <div className="space-y-2">
              <Label>Campaign Images</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateAIImages}
                  disabled={generatingImages || !prompt.trim()}
                  className="flex-1"
                >
                  {generatingImages ? (
                    <>Generating AI Images...</>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate AI Images
                    </>
                  )}
                </Button>
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Generate professional roofing photos with AI or upload your own
              </p>

              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {uploadedImages.map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={url} 
                        alt={`Campaign ${idx + 1}`}
                        className="w-full h-20 object-cover rounded border"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleRemoveImage(url)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button 
              onClick={handleGenerate} 
              disabled={loading || !prompt.trim()}
              className="w-full"
            >
              {loading ? (
                <>Generating Campaign...</>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Campaign
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Card className="p-4 border-primary/20 bg-primary/5">
              <h3 className="font-semibold text-lg mb-1">{generatedCampaign.campaign.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{generatedCampaign.campaign.description}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{editedSteps.length} Steps</Badge>
                <Badge variant="outline">Draft</Badge>
              </div>
            </Card>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base">Email Sequence</Label>
                <Button variant="ghost" size="sm" onClick={handleRegenerate}>
                  <Sparkles className="mr-1 h-3 w-3" />
                  Regenerate
                </Button>
              </div>

              {editedSteps.map((step, idx) => (
                <Card key={idx} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">Step {step.step_number}</Badge>
                      {editingStep !== idx && (
                        <span className="text-xs text-muted-foreground">
                          {step.delay_days} day{step.delay_days !== 1 ? 's' : ''} delay
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {editingStep === idx ? (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleSaveStep(idx)}
                          disabled={loading}
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingStep(idx)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {editingStep === idx ? (
                      <>
                        <div>
                          <Label className="text-xs text-muted-foreground">Delay (days)</Label>
                          <Input
                            type="number"
                            min={0}
                            value={step.delay_days}
                            onChange={(e) => handleEditStep(idx, 'delay_days', parseInt(e.target.value) || 0)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Subject Line</Label>
                          <Input
                            value={step.subject_line}
                            onChange={(e) => handleEditStep(idx, 'subject_line', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Email Preview</Label>
                          <div 
                            className="border rounded p-4 bg-background mt-1 max-h-96 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: step.email_body }}
                          />
                          <details className="mt-2">
                            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                              Show HTML code (advanced)
                            </summary>
                            <Textarea
                              value={step.email_body}
                              onChange={(e) => handleEditStep(idx, 'email_body', e.target.value)}
                              rows={8}
                              className="mt-2 font-mono text-xs"
                            />
                          </details>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label className="text-xs text-muted-foreground">Subject</Label>
                          <p className="font-medium">{step.subject_line}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Email Preview</Label>
                          <div 
                            className="border rounded p-4 bg-background mt-1 max-h-96 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: step.email_body }}
                          />
                        </div>
                        {step.personalization_tokens && step.personalization_tokens.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {step.personalization_tokens.map((token, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {token}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleRegenerate}>
                Start Over
              </Button>
              <Button onClick={handleApprove} disabled={loading}>
                Approve & Activate
              </Button>
            </div>
          </div>
        )}
        </DialogContent>
      </Dialog>
    </>
  );
}